import { useCallback, useEffect, useMemo, useState } from "react";
import { useGlobalAPIQueryContext } from "@dev2_techwalnut/tw-api-query-hook";
import Swal from "sweetalert2";
import MachinePipelineCard from "./MachinePipelineCard";
import "./LiveSite.css";

const unwrap = (response) =>
    response?.data?.results?.data ?? response?.data?.data ?? response?.data;

const toast = (icon, title) =>
    Swal.fire({
        toast: true,
        position: "top-end",
        icon,
        title,
        timer: 2800,
        showConfirmButton: false,
    });

const LiveSiteConfiguration = ({ canEdit = false }) => {
    const { apiClient } = useGlobalAPIQueryContext();
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        const response = await apiClient.get("/api/live-site/configuration/");
        setRows(unwrap(response) || []);
    }, [apiClient]);

    useEffect(() => {
        setLoading(true);
        load()
            .catch((error) => {
                Swal.fire(
                    "Could not load configuration",
                    error?.response?.data?.results?.message || error.message,
                    "error"
                );
            })
            .finally(() => setLoading(false));
    }, [load]);

    const applyRow = (updated) => {
        setRows((current) =>
            current.map((row) => (row.machine_id === updated.machine_id ? { ...row, ...updated } : row))
        );
        if (updated.warning) {
            toast("warning", updated.warning);
        }
    };

    const patch = async (machineId, body) => {
        if (!canEdit) return;
        try {
            const response = await apiClient.patch(`/api/live-site/configuration/${machineId}/`, body);
            applyRow(unwrap(response));
        } catch (error) {
            Swal.fire(
                "Update failed",
                error?.response?.data?.results?.message || error.message,
                "error"
            );
        }
    };

    const visible = useMemo(() => {
        const q = search.toLowerCase();
        return rows.filter((row) =>
            [row.machine_name, row.node_id, row.location_path].join(" ").toLowerCase().includes(q)
        );
    }, [rows, search]);

    if (loading) {
        return <p className="text-soft">Loading configuration…</p>;
    }

    return (
        <>
            <div className="live-site-config-toolbar">
                <input
                    className="form-control"
                    placeholder="Filter machines, nodes, or floor / area"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </div>
            <div className="live-stack">
                {visible.map((row) => (
                    <MachinePipelineCard
                        key={row.machine_id}
                        row={row}
                        canEdit={canEdit}
                        onPatch={patch}
                    />
                ))}
                {visible.length === 0 ? (
                    <section className="card card-inner">
                        <p className="mb-0">No machines match this filter.</p>
                    </section>
                ) : null}
            </div>
        </>
    );
};

export default LiveSiteConfiguration;
